import { PATH_KEYS } from "@/services/appServices";
import { useItemsCrud } from "./useItemsCrud";
import { useItemService } from "./useServices";
import { InvitedUser, User, UserWithRole } from "@/models/users/User";
import {
  keepPreviousData,
  useMutation,
  useQuery,
} from "@tanstack/react-query";
import { Pagination } from "@/models/base/Pagination";
import { useQueryClient } from "@tanstack/react-query";
import { TenantRole } from "@/models/tenant/tenant";
import {
  ConnectionResponse,
  DeleteInvitedUserRequest,
  InviteUserRequest,
} from "@/models/user-management/UserManagement";
import type {
  InvitedUserSortBy,
  UserSortBy,
} from "@/services/userManagementService";
import { restError } from "@/models/client/RestResponse";

const useUserManagement = () => {
  const queryClient = useQueryClient();
  /**** Services ****/
  const userManagementService = useItemService(
    (services) => services.userManagement
  );

  const userCrud = useItemsCrud<User>(
    [PATH_KEYS.userManagement],
    (services) => services.userManagement
  );

  /**** Queries & Mutations ****/
  /** `pageNumber` is one-based, matching the API. */
  const useQueryUsersWithRoles = (
    pageNumber: number = 1,
    pageSize: number = 20,
    search?: string,
    sortBy: UserSortBy = "name",
    sortAscending: boolean = true
  ) =>
    useQuery<Pagination<UserWithRole>>({
      queryKey: [
        PATH_KEYS.userManagement,
        pageNumber,
        pageSize,
        search,
        sortBy,
        sortAscending,
      ],
      queryFn: async () => {
        const fetchedPageContent =
          await userManagementService.getUsersWithRoles(
            pageNumber,
            pageSize,
            search,
            sortBy,
            sortAscending
          );
        if (fetchedPageContent.success) {
          return fetchedPageContent.value;
        } else throw restError(fetchedPageContent);
      },
      // Sorting and paging change the key. Keep the rows already on screen
      // so the table can flag them as stale instead of blanking (LAR-410).
      placeholderData: keepPreviousData,
    });

  /** `pageNumber` is one-based, matching the API. */
  const useQueryInvitedUsers = (
    pageNumber: number = 1,
    pageSize: number = 20,
    sortBy: InvitedUserSortBy = "email",
    sortAscending: boolean = true
  ) =>
    useQuery<Pagination<InvitedUser>>({
      queryKey: [
        PATH_KEYS.userManagement,
        "invited-users",
        pageNumber,
        pageSize,
        sortBy,
        sortAscending,
      ],
      queryFn: async () => {
        const fetchedPageContent = await userManagementService.getInvitedUsers(
          pageNumber,
          pageSize,
          sortBy,
          sortAscending
        );
        if (fetchedPageContent.success) {
          const value = fetchedPageContent.value;
          // The API reports totalRecords; the table reads totalCount.
          return {
            ...value,
            totalCount: value.totalCount ?? value.totalRecords ?? 0,
          };
        } else throw restError(fetchedPageContent);
      },
      placeholderData: keepPreviousData,
    });

  const useQueryConnectionDetails = () =>
    useQuery<ConnectionResponse[]>({
      queryKey: [PATH_KEYS.userManagement, "connection-details"],
      queryFn: async () => {
        const fetchedPageContent =
          await userManagementService.getConnectionDetails();
        if (fetchedPageContent.success) {
          return fetchedPageContent.value;
        } else throw restError(fetchedPageContent);
      },
    });

  const updateUserRoles = useMutation({
    mutationKey: [PATH_KEYS.userManagement, "update-role"],
    mutationFn: async ({
      userId,
      tenantId,
      roles,
    }: {
      userId: string;
      tenantId: string;
      roles: TenantRole[];
      pageSize: number;
      pageNumber: number;
    }) => {
      const fetchedPageContent = await userManagementService.updateUserRoles(
        userId,
        tenantId,
        roles
      );
      if (fetchedPageContent.success) {
        return fetchedPageContent.value;
      } else throw restError(fetchedPageContent);
    },
  });

  const inviteUsers = useMutation({
    mutationKey: [PATH_KEYS.userManagement, "invite-user"],
    mutationFn: async (request: InviteUserRequest) => {
      const fetchedPageContent =
        await userManagementService.inviteUser(request);
      if (fetchedPageContent.success) {
        return fetchedPageContent.value;
      } else throw restError(fetchedPageContent);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.userManagement, "invited-users"],
      });
    },
  });

  const deleteInvitedUser = useMutation({
    mutationKey: [PATH_KEYS.userManagement, "delete-invited-user"],
    mutationFn: async (request: DeleteInvitedUserRequest) => {
      const fetchedPageContent =
        await userManagementService.deleteInvitedUser(request);
      if (fetchedPageContent.success) {
        return fetchedPageContent.value;
      } else throw restError(fetchedPageContent);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.userManagement, "invited-users"],
      });
    },
  });

  const deleteUser = useMutation({
    mutationKey: [PATH_KEYS.userManagement, "delete-user"],
    mutationFn: async ({ userId }: { userId: string }) => {
      const response = await userManagementService.deleteUser(userId);
      if (response.success) {
        return response.value;
      } else throw restError(response);
    },
    onSuccess: () => {
      // Invalidate all user management queries to refresh the users list
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.userManagement],
      });
    },
    onError: (error) => {
      void error;
    },
  });

  /**** Value ****/
  return {
    ...userCrud,
    useQueryUsersWithRoles,
    useQueryInvitedUsers,
    useQueryConnectionDetails,
    updateUserRoles,
    inviteUsers,
    deleteInvitedUser,
    deleteUser,
  };
};
export default useUserManagement;
